import isDate from 'date-fns/isDate';
import isFuture from 'date-fns/isFuture';
import React, { memo } from 'react';
import type { Control } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import type { IItem } from '@/modules/car/model';
import type { VehicleCreateForm } from '@/modules/car/model/car-form';
import { CountryCode, TCountryCodes } from '@/shared/models/country-code';
import { BottomButtons } from '@/shared/view/BottomButtons';
import { LicensingAuthorityField, PHVField } from '@/shared/view/LicensingFields';
import { Section } from '@/shared/view/Section';
import { Button } from '@src-common/view/adapters/Button';
import { DateField } from '@src-common/view/adapters/reactHookForm/DateField';
import { SelectField } from '@src-common/view/adapters/reactHookForm/SelectField';
import { TextField } from '@src-common/view/adapters/reactHookForm/TextField';
import {
  DocsList,
  IDocsListItem,
  HandleDocListItemClick,
} from '@src-common/view/components/DocsList';

import { messages } from './messages';
import s from './styles.module.css';

type Options = Record<
  | 'makes'
  | 'models'
  | 'generations'
  | 'years'
  | 'colors'
  | 'interiorColors'
  | 'wheelbases'
  | 'licensingAuthorities'
  | 'engines',
  IItem[]
>;

interface IProps {
  docs: IDocsListItem[];
  onDocListItemClick: HandleDocListItemClick;
  onSubmit: () => void;
  textSubmitButton?: string;
  isSubmitting?: boolean;
  countryCode: TCountryCodes;
  control: Control<VehicleCreateForm>;
  options: Options;
}

const VehicleFormView: React.FC<IProps> = memo(
  ({
    isSubmitting,
    onDocListItemClick,
    docs,
    onSubmit,
    textSubmitButton,
    countryCode,
    control,
    options,
  }) => {
    const intl = useIntl();

    return (
      <form onSubmit={onSubmit}>
        <Section>
          <SelectField
            control={control}
            name="make"
            title={intl.formatMessage(messages.makeTitle)}
            items={options.makes}
            isRequired={true}
          />
          <SelectField
            control={control}
            name="model"
            title={intl.formatMessage(messages.modelTitle)}
            items={options.models}
            isRequired={true}
          />
          <SelectField
            control={control}
            name="engineType"
            title={intl.formatMessage(messages.engineTitle)}
            items={options.engines}
            isRequired={options.engines.length > 0}
          />
          <SelectField
            control={control}
            name="generation"
            title={intl.formatMessage(messages.generationTitle)}
            items={options.generations}
            isRequired={options.generations.length > 0}
          />
          <SelectField
            control={control}
            name="year"
            title={intl.formatMessage(messages.yearTitle)}
            items={options.years}
            isRequired={true}
          />
          <SelectField
            control={control}
            name="color"
            title={intl.formatMessage(messages.colorTitle)}
            items={options.colors}
            isRequired={true}
          />
          <SelectField
            control={control}
            name="interiorColor"
            title={intl.formatMessage(messages.interiorColorTitle)}
            items={options.interiorColors}
            isRequired={true}
          />
          <SelectField
            control={control}
            name="wheelbase"
            title={intl.formatMessage(messages.wheelbaseTitle)}
            description={intl.formatMessage(messages.wheelbaseDescription)}
            items={options.wheelbases}
            isRequired={options.wheelbases.length > 0}
          />

          <DateField
            control={control}
            name="registrationDate"
            title={intl.formatMessage(messages.registrationDateTitle)}
            isRequired={true}
            rules={{
              validate: {
                notInTheFuture: value => {
                  if (isDate(value) && isFuture(value as Date)) {
                    return intl.formatMessage(messages.registrationDateNotInFuture);
                  }
                },
              },
            }}
          />
          {countryCode === CountryCode.GreatBritain && (
            <LicensingAuthorityField control={control} />
          )}
          <TextField
            control={control}
            name="licensePlate"
            title={intl.formatMessage(messages.licensePlateTitle)}
            placeholder={
              countryCode === CountryCode.UnitedStates
                ? undefined
                : intl.formatMessage(messages.licensePlatePlaceholder)
            }
            isRequired={true}
            description={intl.formatMessage(messages.licensePlateDescription)}
          />
          <TextField
            control={control}
            name="vin"
            title={intl.formatMessage(messages.vinTitle)}
            isRequired={true}
            rules={{
              validate: {
                vinLength: value => {
                  if (countryCode !== CountryCode.UnitedStates) {
                    return;
                  }

                  if (typeof value === 'string' && value.trim().length !== 17) {
                    return intl.formatMessage(messages.vinUsLength17);
                  }
                },
              },
            }}
          />
          {countryCode === CountryCode.UnitedStates && (
            <TextField
              control={control}
              name="licenseNumber"
              title={intl.formatMessage(messages.tlcVehicleLicense)}
              isRequired={true}
              rules={{
                pattern: {
                  value: /^\d{7}$/,
                  message: intl.formatMessage(messages.tlcVehicleLicenseError),
                },
              }}
            />
          )}
          {countryCode === CountryCode.GreatBritain && <PHVField control={control} />}
          <DocsList
            title={intl.formatMessage(messages.addDocumentsTitle)}
            className={s.docs}
            docs={docs}
            onChange={onDocListItemClick}
            isStatusHidden={true}
          />
        </Section>
        <BottomButtons>
          <Button type="submit" isPending={isSubmitting}>
            {textSubmitButton || <FormattedMessage {...messages.nextButtonText} />}
          </Button>
        </BottomButtons>
      </form>
    );
  },
);

VehicleFormView.displayName = 'VehicleFormView';

export { VehicleFormView };
